import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorNotice } from '../../components/ErrorNotice.js';
import {
  ActionButton,
  DateField,
  Dialog,
  SelectField,
  TextAreaField,
  TextField,
  type SelectOption,
} from '../../components/ui/index.js';

/**
 * One recorded act in a dialog — raise, close, complete, verify, cancel,
 * schedule, resolve (Phase 12).
 *
 * The dialog closes only when the SERVER has done it (CLAUDE.md section 8): a
 * refusal stays on screen, in words from the error catalogue, and nothing on
 * the page changes until the act is recorded.
 */

export interface ActField {
  readonly name: string;
  /** Already translated. */
  readonly label: string;
  readonly kind: 'text' | 'textarea' | 'date' | 'select';
  readonly description?: string;
  readonly isRequired?: boolean;
  readonly options?: readonly SelectOption[];
  readonly initial?: string | null;
}

export type ActValues = Readonly<Record<string, string | null>>;

export interface ActDialogProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly title: string;
  /** What will actually happen, in words. Already translated. */
  readonly hint?: string;
  readonly fields: readonly ActField[];
  readonly submitLabel: string;
  readonly destructive?: boolean;
  /** Resolves when the server has recorded the act. */
  readonly onSubmit: (values: ActValues) => Promise<unknown>;
}

export function ActDialog({
  isOpen,
  onClose,
  title,
  hint,
  fields,
  submitLabel,
  destructive = false,
  onSubmit,
}: ActDialogProps) {
  const { t } = useTranslation();
  const initial = () => Object.fromEntries(fields.map((field) => [field.name, field.initial ?? null]));
  const [values, setValues] = useState<Record<string, string | null>>(initial);
  const [error, setError] = useState<unknown>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setValues(initial());
      setError(null);
    }
    // Reset only when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const set = (name: string) => (value: string | null) =>
    setValues((current) => ({ ...current, [name]: value === '' ? null : value }));

  const missing = fields.some((field) => field.isRequired && !values[field.name]?.trim());

  const submit = async () => {
    setPending(true);
    setError(null);
    try {
      await onSubmit(values);
      onClose();
    } catch (caught) {
      setError(caught);
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      footer={
        <>
          <ActionButton priority="tertiary" onPress={onClose} isDisabled={pending}>
            {t('common.cancel')}
          </ActionButton>
          <ActionButton
            priority={destructive ? 'destructive' : 'primary'}
            onPress={() => void submit()}
            isLoading={pending}
            isDisabled={missing}
          >
            {submitLabel}
          </ActionButton>
        </>
      }
    >
      <div className="flex flex-col gap-stack">
        {hint ? <p className="text-body text-default-700">{hint}</p> : null}
        {fields.map((field) => {
          const common = {
            label: field.label,
            isRequired: field.isRequired ?? false,
            ...(field.description ? { description: field.description } : {}),
          };
          switch (field.kind) {
            case 'textarea':
              return (
                <TextAreaField
                  key={field.name}
                  {...common}
                  value={values[field.name] ?? ''}
                  onChange={set(field.name)}
                />
              );
            case 'date':
              return (
                <DateField key={field.name} {...common} value={values[field.name] ?? null} onChange={set(field.name)} />
              );
            case 'select':
              return (
                <SelectField
                  key={field.name}
                  {...common}
                  value={values[field.name] ?? null}
                  onChange={set(field.name)}
                  options={field.options ?? []}
                />
              );
            default:
              return (
                <TextField key={field.name} {...common} value={values[field.name] ?? ''} onChange={set(field.name)} />
              );
          }
        })}
        {error ? <ErrorNotice error={error} /> : null}
      </div>
    </Dialog>
  );
}
